import React from 'react';
import ReactMarkdown from 'react-markdown';

import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';
import { Link } from '@components/parts/Link';

import { DynamicData } from '@customTypes/DynamicData';
import { LinkProps } from '@customTypes/Link';

type Props = {
  titleHint: string;
  title: string;
  description?: string;
  action?: LinkProps;
  dynamicData?: DynamicData;
};

const Wrapper = styled.div`
  padding-top: 90px;
  div.up {
    display: flex;
    flex-direction: column;
    div.table-container {
      flex: 1;
      display: flex;
      justify-content: right;
      div {
        width: 100%;
      }
      table {
        margin-top: 24px;
        border-collapse: collapse;
        padding-top: 50px;
        width: 100%;

        font-size: 1rem;
        border-spacing: 0;
        th {
          border: 1px solid #e3e5ea;
          padding: 14px 32px;
          font-weight: 500;
          background: #f3f4f8;
          color: #62656a;
          text-align: center;
        }
        td {
          color: #2e3035;
          border: 1px solid #e3e5ea;
          font-weight: 500;
          text-align: center;
          padding: 21px 8px;
          strong {
            color: #055cf5;
          }
          em {
            display: block;
            color: #62656a;
            font-weight: 400;
            font-style: normal;
          }
        }
      }
      p {
        margin-top: 1.5em;
        color: #a3a7ae;
        font-size: 14px;
        @media (min-width: 992px) {
          font-size: 16px;
        }
        font-weight: 500;
      }
    }
  }
  @media (min-width: 992px) {
    div.up {
      flex-direction: row;
      .link {
        display: inline-flex;
      }
      div.table-container {
        margin-left: auto;
        max-width: 540px;
        table {
          margin-top: 0;
          padding-top: 0;
        }
      }
    }
    padding-top: 80px;
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    white-space: pre;
  }
  .title-hint {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 500;
    opacity: 0.8;
    @media (min-width: 992px) {
      font-size: 24px;
    }
  }
  .link {
    margin-top: 30px;
    margin-bottom: 40px;
    @media (max-width: 430px) {
      font-size: 15px;
      height: 44px;
    }
    font-size: 16px;
    height: 46px;
  }
  p {
    color: rgba(0, 0, 0, 0.8);
    color: rgba(var(--text-color), 0.4);
    font-weight: 700;
    strong {
      color: rgba(0, 0, 0);
      color: rgba(var(--text-color), 1);
    }
    margin-top: 0.5em;
    font-size: 20px;
    @media (min-width: 992px) {
      font-size: 26px;
    }
  }
`;

export const ExpectedCost: React.FC<Props> = (props) => {
  const {
    // prettier-ignore
    titleHint,
    title,
    description,
    action,
    dynamicData,
  } = props;
  if (!dynamicData?.pricing) return null;

  return (
    <Container style={dynamicData.display_name === '이사' ? { marginBottom: 160 } : {}}>
      <Wrapper>
        <div className="up">
          {titleHint ? (
            <ScrollReveal>
              <div className="title-hint">{titleHint}</div>
              <h3 style={{ marginRight: 60 }}>{title}</h3>
              {description ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {description}
                </ReactMarkdown>
              ) : null}
              {action ? <Link {...action} /> : null}
            </ScrollReveal>
          ) : (
            <ScrollReveal>
              <h2>{title}</h2>
              {description ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {description}
                </ReactMarkdown>
              ) : null}
              {action ? <Link {...action} /> : null}
            </ScrollReveal>
          )}
          <ScrollReveal intervalDelay={500} className="table-container">
            <div>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {dynamicData.pricing}
              </ReactMarkdown>
              {dynamicData?.pricingHint ? (
                <ScrollReveal>
                  <p>{dynamicData.pricingHint}</p>
                </ScrollReveal>
              ) : null}
            </div>
          </ScrollReveal>
        </div>
      </Wrapper>
    </Container>
  );
};
